import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import {
  defaultHolidayPayCalcOptions,
  getHolidayPaymentMethodOptions,
  leaveYearStartDateOptions,
  yesNoNAOptions,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
import { HolidayMethodEnum, YesNoNAEnum } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { Col, Form, Row } from 'antd';
import React, { Ref, useImperativeHandle, useMemo } from 'react';
import AppPercentageInput from '@nxsys/components/AppPercentageInput';

export type PayrollSettingFormValues = {
  timesheetApprovalNeeded: YesNoNAEnum | null;
  listEmprApprovers: Option<string>[] | null;
  payrollApproveNeeded: YesNoNAEnum | null;
  enableHolidayPay: boolean;
  payeHolidayPayRate: string;
  holidayPayMethod: Option<string> | null;
  holidayPayCalculationRule: Option<string> | null;
  includeInTimesheetRate: boolean | null;
  // NX-6284: Ireland-only leave year start, default 1 April. Encoded as
  // "day-month" (toLeaveYearStart/fromLeaveYearStart) so one dropdown
  // carries both numbers the API still wants.
  holidayLeaveYearStart: Option<string> | null;
};

export type PayrollSettingFormRef = {
  submit: () => void;
};

type PayrollSettingFormProps = {
  formRef?: Ref<PayrollSettingFormRef>;
  onSubmit?: (values: PayrollSettingFormValues) => void;
  defaultValues?: Partial<PayrollSettingFormValues>;
  companyId?: string;
  isIreland?: boolean;
};

const PayrollSettingForm: React.FC<PayrollSettingFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
  companyId,
  isIreland = false,
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm<PayrollSettingFormValues>();
  const watchValues = Form.useWatch([], form);

  // Ireland has no 52-week-average reference period — excluded for Ireland
  // (a saved legacy value stays visible), with the 8% irregular method added
  // (NX-6284).
  const holidayPayMethodOptions = useMemo(
    () => getHolidayPaymentMethodOptions(isIreland, watchValues?.holidayPayMethod?.value),
    [isIreland, watchValues?.holidayPayMethod?.value],
  );

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  // NX-6327: apply the 52 Week Average defaults only when the user picks it.
  // onValuesChange skips setFieldsValue/initialValues, so saved values survive load.
  const onValuesChange = (changed: any) => {
    if (
      changed?.holidayPayMethod?.value ===
      HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE
    ) {
      const allEarnings = defaultHolidayPayCalcOptions.find(
        (opt) => opt.value === 'ALL_EARNINGS',
      );
      if (allEarnings) {
        form.setFieldValue('holidayPayCalculationRule', allEarnings);
      }
      form.setFieldValue('includeInTimesheetRate', true);
    }
  };

  const onFinish = (values: PayrollSettingFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      onValuesChange={onValuesChange}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      {...(defaultValues ? { initialValues: defaultValues } : {})}
    >
      <Row gutter={[16, 0]}>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.holidayPay']}
            name="enableHolidayPay"
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={`${messages['common.holidayPayRate']} %`}
            name="payeHolidayPayRate"
          >
            <AppPercentageInput
              placeholder={messages['form.enter']}
              disabled={!watchValues?.enableHolidayPay}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.holidayPayMethod']}
            name="holidayPayMethod"
          >
            <AppSelect
              options={holidayPayMethodOptions}
              placeholder={messages['form.select']}
              disabled={!watchValues?.enableHolidayPay}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.holidayPayCalculationRule']}
            name="holidayPayCalculationRule"
          >
            <AppSelect
              options={defaultHolidayPayCalcOptions}
              placeholder={messages['form.select']}
              disabled={!watchValues?.enableHolidayPay}
            />
          </Form.Item>
        </Col>
        {watchValues?.holidayPayMethod &&
          watchValues.holidayPayMethod.value !==
            HolidayMethodEnum.NO_HOLIDAY_PAY && (
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.includedTimesheetRate']}
                name="includeInTimesheetRate"
              >
                <AppRadioGroup
                  options={yesNoOptions}
                  disabled={!watchValues?.enableHolidayPay}
                />
              </Form.Item>
            </Col>
          )}
        {/* NX-6284: Ireland-only leave year start, default 1 April — sits
            with the Holiday Pay Method it feeds. One searchable dropdown
            over every calendar date (29 Feb excluded). */}
        {isIreland && (
          <Col sm={24} md={12}>
            <Form.Item
              label={
                messages['common.holidayLeaveYearStart'] ??
                'Leave Year Start'
              }
              name="holidayLeaveYearStart"
            >
              <AppSelect
                options={leaveYearStartDateOptions}
                placeholder={messages['form.select']}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
      <Row gutter={16}>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.timesheetApprovalNeeded']}
            name="timesheetApprovalNeeded"
            valuePropName="value"
          >
            <AppRadioGroup options={yesNoNAOptions} />
          </Form.Item>
        </Col>
        {watchValues?.timesheetApprovalNeeded === YesNoNAEnum.YES && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.timesheetApprover']}
              name="listEmprApprovers"
            >
              <AppSelectSync
                type="approver"
                apiType="searchUserAdminCompany"
                placeholder={messages['form.select']}
                style={{ width: '100%' }}
                onChangeValue={(value: any) => {
                  form.setFieldValue('listEmprApprovers', value);
                }}
                mode="multiple"
                paramFilter={{
                  companyUserIds: [companyId],
                  roles: ['company'],
                }}
              />
            </Form.Item>
          </Col>
        )}
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.payrollApprovalNeeded']}
            name="payrollApproveNeeded"
            valuePropName="value"
          >
            <AppRadioGroup options={yesNoNAOptions} />
          </Form.Item>
        </Col>
      </Row>
    </AppFormContainer>
  );
};

export default PayrollSettingForm;
